import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { ClipboardEdit, CheckCircle2, Clock, User, ArrowRight, Search, ShieldAlert } from "lucide-react";

/**
 * Clinician view of every patient intake tied to an assessment in the
 * clinician's caseload. Two lists: pending (awaiting patient submission)
 * and submitted (ready to review).
 */
export default function ClinicianIntakes() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clinician/intakes")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) =>
    !q.trim() || (r.patient_name || "").toLowerCase().includes(q.trim().toLowerCase())
  );
  const referrals = filtered.filter((r) => r.source === "programme_screening");
  const nonReferral = filtered.filter((r) => r.source !== "programme_screening");
  const pending = nonReferral.filter((r) => r.intake_status !== "submitted");
  const submitted = nonReferral.filter((r) => r.intake_status === "submitted");

  return (
    <DashLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">Clinician tools</div>
          <h1 className="fl-heading text-4xl mt-2">Patient intakes</h1>
          <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
            Every intake questionnaire attached to an assessment in your caseload.
            Pending intakes appear in the patient&apos;s Forms tab automatically.
          </p>
        </div>
        <div className="fl-card p-2 flex items-center gap-2 min-w-[240px]">
          <Search size={14} style={{ color: "var(--fl-muted)" }} />
          <input
            className="fl-input border-0 text-sm flex-1"
            placeholder="Search by patient…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="clinician-intake-search"
          />
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <SummaryTile label="Screening referrals" value={referrals.length} tone="amber" icon={<ShieldAlert size={14} />} />
        <SummaryTile label="Pending" value={pending.length} tone="amber" icon={<Clock size={14} />} />
        <SummaryTile label="Submitted" value={submitted.length} tone="green" icon={<CheckCircle2 size={14} />} />
        <SummaryTile label="Total" value={filtered.length} tone="neutral" icon={<ClipboardEdit size={14} />} />
      </div>

      {/* Phase 1E Gap 1 — AMBER online-programme screenings routed here for review */}
      <ReferralSection rows={referrals} loading={loading} />

      <IntakeSection
        title="Pending"
        rows={pending}
        empty="No intakes waiting on a patient. Nice."
        loading={loading}
      />
      <IntakeSection
        title="Submitted"
        rows={submitted}
        empty="Nothing submitted yet."
        loading={loading}
      />
    </DashLayout>
  );
}

function ReferralSection({ rows, loading }) {
  if (loading) return null;
  if (!rows.length) return null;
  return (
    <div className="mb-8" data-testid="screening-referrals-section">
      <div className="fl-heading text-xl mb-3 flex items-center gap-2">
        <ShieldAlert size={18} style={{ color: "#eab308" }} /> Screening referrals ({rows.length})
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <Link
            key={r.assessment_id}
            to={`/clinician/assessment?assessment=${r.assessment_id}`}
            className="fl-card p-5 hover:translate-y-[-1px] transition-transform"
            style={{ borderLeft: "3px solid #eab308" }}
            data-testid={`screening-referral-${r.assessment_id}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "color-mix(in oklab, #eab308 22%, var(--fl-bg-2))", color: "#eab308" }}
              >
                <ShieldAlert size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="fl-heading text-base flex items-center gap-2">
                  <User size={14} style={{ color: "var(--fl-muted)" }} />
                  {r.patient_name || "Unknown patient"}
                </div>
                <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
                  Amber screening · {r.programme_name || "Online programme"}
                  {r.submitted_at ? ` · ${new Date(r.submitted_at).toLocaleDateString("en-GB")}` : ""}
                </div>
                {(r.amber_flags || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {(r.amber_flags || []).slice(0, 3).map((f, i) => (
                      <li key={i} className="text-[11px] flex items-start gap-1.5" style={{ color: "var(--fl-muted)" }}>
                        <span style={{ color: "#eab308" }}>•</span> {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <ArrowRight size={14} style={{ color: "var(--fl-muted-2)" }} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone, icon }) {
  const palette = {
    amber:   { bg: "rgba(234,179,8,.10)", border: "rgba(234,179,8,.35)", text: "#eab308" },
    green:   { bg: "rgba(34,197,94,.10)", border: "rgba(34,197,94,.35)", text: "#22c55e" },
    neutral: { bg: "rgba(255,255,255,.05)", border: "rgba(255,255,255,.10)", text: "var(--fl-muted)" },
  }[tone] || {};
  return (
    <div className="p-4 rounded-md" style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold" style={{ color: palette.text }}>
        {icon} {label}
      </div>
      <div className="text-3xl font-bold mt-2" style={{ color: "var(--fl-text)" }}>{value}</div>
    </div>
  );
}

function IntakeSection({ title, rows, empty, loading }) {
  return (
    <div className="mb-8">
      <div className="fl-heading text-xl mb-3">{title} ({rows.length})</div>
      {loading && <div className="fl-card p-6 text-sm text-zinc-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted-2)" }}>{empty}</div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <Link
            key={r.assessment_id}
            to={`/clinician/assessment?assessment=${r.assessment_id}`}
            className="fl-card p-5 hover:translate-y-[-1px] transition-transform"
            data-testid={`clin-intake-row-${r.assessment_id}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: r.intake_status === "submitted"
                    ? "color-mix(in oklab, #22c55e 22%, var(--fl-bg-2))"
                    : "color-mix(in oklab, #eab308 22%, var(--fl-bg-2))",
                  color: r.intake_status === "submitted" ? "#22c55e" : "#eab308",
                }}
              >
                {r.intake_status === "submitted" ? <CheckCircle2 size={16} /> : <Clock size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="fl-heading text-base flex items-center gap-2">
                  <User size={14} style={{ color: "var(--fl-muted)" }} />
                  {r.patient_name || "Unknown patient"}
                </div>
                <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
                  {r.intake_status === "submitted"
                    ? `Submitted ${new Date(r.submitted_at).toLocaleDateString("en-GB")} · ${r.fields_completed} fields`
                    : `Awaiting patient · assessment opened ${new Date(r.created_at).toLocaleDateString("en-GB")}`}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold mt-2"
                     style={{ color: "var(--fl-muted-2)" }}>
                  Assessment status · {r.assessment_status}
                </div>
              </div>
              <ArrowRight size={14} style={{ color: "var(--fl-muted-2)" }} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
